import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { useCallback } from 'react';
import { AddFriendModal } from '../../panels/friendsPanel/addFriendModal';
import { RemoveFriendModal } from '../../panels/friendsPanel/removeFriendModal';
import { thunkCloseCurrentMessageBox } from '../../redux/actions/messageBoxesActions';
import { useAppDispatch } from '../../redux/store';
import { MESSAGE_BOX_RED_FONT_REPLACEMENT_COLOR } from '../../styles/colors';
import { getHtmlTextContent } from '../../utils/htmlUtils';
import { assertUnreachable } from '../../utils/tsUtils';
import { HtmlRenderer } from '../htmlAreas/htmlRenderer';
import { OpenModalContainer } from '../openModalContainer';
import { AccountModal } from './accountModal';
import { AdminManualMessageBoxContents } from './adminManualMessageBoxContents';
import { ChangeEmailModal } from './changeEmailModal';
import { ChangePasswordModal } from './changePasswordModal';
import { CommunityModal } from './communityModal';
import { ConfirmWindowMessageBoxContents } from './confirmWindowMessageBoxContents';
import { ContactUsModal } from './contactUsModal';
import { DevToolsMessageBoxContents } from './devToolsMessageBoxContents';
import { ExternalLinkMessageBoxContents } from './externalLinkMessageBoxContents';
import { FindPlayerMessageBoxContents } from './findPlayerMessageBoxContents';
import { FreeDailyBonusSuccessModal } from './freeDailyBonusSuccessModal';
import { GameGuideModal } from './gameGuideModal';
import { GiveChipsModal } from './giveChipsModal';
import { GiveJacksMessageBoxContents } from './giveJacksMessageBoxContents';
import { GoToPathModal } from './goToPathModal';
import { IncomeEventMessageBoxContents } from './incomeEventMessageBoxContents';
import { JacksToChipsModal } from './jacksToChipsModal';
import { MobileBetaWarningModal } from './mobileBetaWarningModal';
import { MttToolsModal } from './mttToolsModal';
import { NeedsPowerPlayerMessageBoxContents } from './needsPowerPlayerMessageBoxContents';
import { NewClientAvailableModal } from './newClientAvailableModal';
import { NewsModalContainer } from './newsModalContainer';
import { OutdatedClientModal } from './outdatedClientModal';
import { PlayerAvatarMessageBoxContents } from './playerAvatarMessageBoxContents';
import { PlayerListMessageBoxContents } from './playerListMessageBoxContents';
import { PlayerQuickActionsModal } from './playerQuickActionsModal';
import { PowerPlayerStatusModal } from './powerPlayerStatusModal';
import { PwaWarningModal } from './pwaWarningModal';
import { RankingListModal } from './rankingListModal';
import { RecentTourneyWinnersMessageBoxContents } from './recentTourneyWinnersMessageBoxContents';
import { RecordMessageBoxContents } from './recordMessageBoxContents';
import { RenamePlayerAdminActionMessageBoxContents } from './renamePlayerAdminActionMessageBoxContents';
import { SelfActionsModal } from './selfActionsModal';
import { SetBuyInMessageBoxContents } from './setBuyInMessageBoxContents';
import { SettingsModal } from './settingsModal/settingsModal';
import { SignUpConfirmationModal } from './signUpConfirmationModal';
import { TableMessageBoxContents } from './tableMessageBoxContents';
import { TestEmailDetailsModal } from './testEmailDetailsModal';
import { TourneyDetailsModal } from './tourneyDetailsModal';
import { TourneySummaryModal } from './tourneySummaryModal';
import { UpcomingTourneysModal } from './upcomingTourneysModal';
import { WebGlLoginWarningMessageBoxContents } from './webGlLoginWarningMessageBoxContents';
export const MessageBoxContainer = ({ messageBox }) => {
    const dispatch = useAppDispatch();
    const onClose = useCallback(() => {
        dispatch(thunkCloseCurrentMessageBox());
    }, [dispatch]);
    switch (messageBox.type) {
        case 'basic': {
            return (_jsx(OpenModalContainer, { title: messageBox.title, onClose: onClose, maxWidth: 'md', children: messageBox.message && (_jsx(DialogContent, { children: _jsx(DialogContentText, { children: messageBox.message }) })) }));
        }
        case 'account': {
            return _jsx(AccountModal, { email: messageBox.email, onClose: onClose });
        }
        case 'power-player-status': {
            const { isPowerPlayer, expirationDateString, allowUpgradeHere, powerPlayerProducts } = messageBox;
            return (_jsx(PowerPlayerStatusModal, { isPowerPlayer: isPowerPlayer, expirationDateString: expirationDateString, allowUpgradeHere: allowUpgradeHere, powerPlayerProducts: powerPlayerProducts, onClose: onClose }));
        }
        case 'change-password': {
            return _jsx(ChangePasswordModal, { title: messageBox.title, onClose: onClose });
        }
        case 'jacks-to-chips': {
            return _jsx(JacksToChipsModal, { jacksToChipsOptions: messageBox.jacksToChipsOptions, onClose: onClose });
        }
        case 'income-event': {
            const messageString = getHtmlTextContent(messageBox.message) || ''; // TODO: Determine if this is HTML and therefore maybe this step is unnecessary.
            return (_jsx(OpenModalContainer, { title: messageString, maxWidth: 'md', children: _jsx(DialogContent, { children: _jsx(IncomeEventMessageBoxContents, { incomeEventId: messageBox.incomeEventId }) }) }));
        }
        case 'confirm': {
            return (_jsx(OpenModalContainer, { title: messageBox.title, maxWidth: 'md', onClose: messageBox.showCloseButton ? onClose : undefined, children: _jsx(DialogContent, { children: _jsx(ConfirmWindowMessageBoxContents, { messageBox: messageBox }) }) }));
        }
        case 'self-actions': {
            return _jsx(SelfActionsModal, { onClose: onClose });
        }
        case 'player-quick-actions': {
            return _jsx(PlayerQuickActionsModal, { player: messageBox.player, onClose: onClose });
        }
        case 'inbound': {
            return (_jsx(OpenModalContainer, { title: messageBox.title, onClose: onClose, maxWidth: 'md', children: messageBox.html && (_jsx(DialogContent, { children: _jsx(DialogContentText, { children: _jsx(HtmlRenderer, { html: messageBox.html, fontColorReplacements: { '#550000': MESSAGE_BOX_RED_FONT_REPLACEMENT_COLOR } }) }) })) }));
        }
        case 'tourney-calendar': {
            return (_jsx(OpenModalContainer, { title: 'Tourney Calendar (CST)', onClose: onClose, maxWidth: 'xl', fullHeight: true, children: _jsx(DialogContent, { children: _jsx("iframe", { src: 'https://calendar.google.com/calendar/embed?height=600&wkst=1&bgcolor=%23ffffff&ctz=America%2FChicago&src=MnRucGltNGlta2xpOTJuZWlwbjV2bHZ0ZTBAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&color=%238E24AA&showDate=0&showTitle=0&showPrint=0&showCalendars=0&mode=AGENDA', css: css `
                border-width: 0;
                width: 100%;
                height: 100%;
              `, frameBorder: '0', scrolling: 'no' }) }) }));
        }
        case 'give-chips': {
            return (_jsx(GiveChipsModal, { toPlayer: messageBox.toPlayer, minChips: messageBox.minChips, maxChips: messageBox.maxChips, taxCurrencyPercent: messageBox.taxCurrencyPercent, untaxedChipsGivenMonthly: messageBox.untaxedChipsGivenMonthly, jacksCost: messageBox.jacksCost, onClose: onClose }));
        }
        case 'give-jacks': {
            return (_jsx(OpenModalContainer, { title: `Give Jacks to ${messageBox.toPlayer.name}`, onClose: onClose, maxWidth: 'sm', children: _jsx(DialogContent, { children: _jsx(GiveJacksMessageBoxContents, { toPlayer: messageBox.toPlayer, onClose: onClose }) }) }));
        }
        case 'settings': {
            return _jsx(SettingsModal, { onClose: onClose });
        }
        case 'community': {
            return _jsx(CommunityModal, { onClose: onClose });
        }
        case 'pwa-warning': {
            return _jsx(PwaWarningModal, { onClose: onClose });
        }
        case 'mobile-beta-warning': {
            return _jsx(MobileBetaWarningModal, { onClose: onClose });
        }
        case 'news': {
            return _jsx(NewsModalContainer, { onClose: onClose, lastNewsItemHash: messageBox.lastNewsItemHash });
        }
        case 'find-player': {
            return (_jsx(OpenModalContainer, { title: 'Find Player', onClose: onClose, maxWidth: 'sm', children: _jsx(DialogContent, { children: _jsx(FindPlayerMessageBoxContents, { onClose: onClose }) }) }));
        }
        case 'external-link': {
            return (_jsx(OpenModalContainer, { title: 'External Link Warning', onClose: onClose, maxWidth: 'sm', children: _jsx(DialogContent, { children: _jsx(ExternalLinkMessageBoxContents, { onClose: onClose, url: messageBox.url }) }) }));
        }
        case 'game-guide': {
            return _jsx(GameGuideModal, { onClose: onClose });
        }
        case 'web-gl-login-warning': {
            return (_jsx(OpenModalContainer, { title: 'WebGL Required', onClose: onClose, children: _jsx(DialogContent, { children: _jsx(WebGlLoginWarningMessageBoxContents, { username: messageBox.username, password: messageBox.password, onClose: onClose }) }) }));
        }
        case 'change-email': {
            return _jsx(ChangeEmailModal, { currentEmail: messageBox.currentEmail, onClose: onClose });
        }
        case 'rename-player-admin-action': {
            return (_jsx(OpenModalContainer, { title: `Rename Player ${messageBox.player.name}`, onClose: onClose, children: _jsx(DialogContent, { children: _jsx(RenamePlayerAdminActionMessageBoxContents, { playerId: messageBox.player.id, onClose: onClose }) }) }));
        }
        case 'set-buy-in': {
            return (_jsx(OpenModalContainer, { title: 'Set Buy-in', onClose: onClose, children: _jsx(DialogContent, { children: _jsx(SetBuyInMessageBoxContents, { seatIndex: messageBox.seatIndex, onClose: onClose }) }) }));
        }
        case 'dev-tools': {
            return (_jsx(OpenModalContainer, { title: 'Dev Tools', onClose: onClose, maxWidth: 'lg', children: _jsx(DialogContent, { children: _jsx(DevToolsMessageBoxContents, {}) }) }));
        }
        case 'player-avatar': {
            const title = messageBox.username ? `${messageBox.username}'s Avatar` : 'Your Avatar';
            return (_jsx(OpenModalContainer, { title: title, onClose: onClose, maxWidth: 'xs', children: _jsx(DialogContent, { children: _jsx(PlayerAvatarMessageBoxContents, { username: messageBox.username, playerAvatar: messageBox.playerAvatar, onClose: onClose }) }) }));
        }
        case 'needs-power-player': {
            return (_jsx(OpenModalContainer, { title: 'Needs Power Player', onClose: onClose, children: _jsx(DialogContent, { children: _jsx(NeedsPowerPlayerMessageBoxContents, { onClose: onClose, powerPlayerFeatureMessageType: messageBox.powerPlayerFeatureMessageType }) }) }));
        }
        case 'ranking-list': {
            return _jsx(RankingListModal, { onClose: onClose, rankingList: messageBox.rankingList });
        }
        case 'add-friend': {
            return _jsx(AddFriendModal, { onClose: onClose });
        }
        case 'remove-friend': {
            return _jsx(RemoveFriendModal, { username: messageBox.username, onClose: onClose });
        }
        case 'recent-tourney-winners': {
            return (_jsx(OpenModalContainer, { title: 'Recent Tourney Winners', onClose: onClose, children: _jsx(DialogContent, { children: _jsx(RecentTourneyWinnersMessageBoxContents, { onClose: onClose, recentTourneyWinners: messageBox.recentTourneyWinners }) }) }));
        }
        case 'record': {
            return (_jsx(OpenModalContainer, { title: messageBox.title, onClose: onClose, children: _jsx(DialogContent, { children: _jsx(RecordMessageBoxContents, { fields: messageBox.fields }) }) }));
        }
        case 'player-list': {
            return (_jsx(OpenModalContainer, { title: messageBox.title, onClose: onClose, children: _jsx(DialogContent, { children: _jsx(PlayerListMessageBoxContents, { players: messageBox.players, onClose: onClose }) }) }));
        }
        case 'admin-manual': {
            return (_jsx(OpenModalContainer, { title: 'Admin Manual', onClose: onClose, maxWidth: 'xl', children: _jsx(DialogContent, { children: _jsx(AdminManualMessageBoxContents, {}) }) }));
        }
        case 'table': {
            return (_jsx(OpenModalContainer, { title: messageBox.title, onClose: onClose, maxWidth: messageBox.maxWidth, children: _jsx(DialogContent, { children: _jsx(TableMessageBoxContents, { description: messageBox.description, headers: messageBox.headers, rows: messageBox.rows, onClose: onClose }) }) }));
        }
        case 'sign-up-confirmation': {
            return (_jsx(SignUpConfirmationModal, { username: messageBox.username, password: messageBox.password, email: messageBox.email, onClose: onClose }));
        }
        case 'tourney-summary': {
            return (_jsx(TourneySummaryModal, { title: messageBox.title, html: messageBox.html, winnerName: messageBox.winnerName, shareWinMessage: messageBox.shareWinMessage, onClose: onClose }));
        }
        case 'outdated-client': {
            return _jsx(OutdatedClientModal, { isLoginConfirmation: messageBox.isLoginConfirmation, onClose: onClose });
        }
        case 'upcoming-tourneys': {
            return (_jsx(UpcomingTourneysModal, { upcomingTourneys: messageBox.upcomingTourneys, refreshTimestamp: performance.now(), onClose: onClose }));
        }
        case 'test-email-details': {
            return _jsx(TestEmailDetailsModal, { emailMessage: messageBox.emailMessage, onClose: onClose });
        }
        case 'mtt-tools': {
            return _jsx(MttToolsModal, { onClose: onClose });
        }
        case 'free-daily-bonus-success': {
            return (_jsx(FreeDailyBonusSuccessModal, { onClose: onClose, numBaseChips: messageBox.numBaseChips, numActivityChips: messageBox.numActivityChips, numStreakChips: messageBox.numStreakChips, powerPlayerAdditionalPercent: messageBox.powerPlayerAdditionalPercent, numTotalChips: messageBox.numTotalChips, streak: messageBox.streak }));
        }
        case 'tourney-details': {
            return _jsx(TourneyDetailsModal, { tourneyDetails: messageBox.tourneyDetails, onClose: onClose });
        }
        case 'go-to-path': {
            return _jsx(GoToPathModal, { onClose: onClose });
        }
        case 'new-client-available': {
            return _jsx(NewClientAvailableModal, { onClose: onClose });
        }
        case 'contact-us': {
            return _jsx(ContactUsModal, { onClose: onClose });
        }
        default: {
            assertUnreachable(messageBox);
        }
    }
};
